import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import kebabCase from 'lodash/kebabCase';
import without from 'lodash/without';
import union from 'lodash/union';
import groupBy from 'lodash/groupBy';

import Form from '../../../../components/Form';
import Checkbox from '../../../../components/Checkbox';
import {
  transferCreditPolicies,
  transferCreditPoliciesComparator,
  TRANSFER_CREDIT_POLICIES_GENERAL,
} from '../../cmsDataMappingHelpers';
import { states } from '../../../../utils/location-utils';
import withCollegeService from '../withCollegeService';
import TextFieldList from './components/textFieldList';
import GroupedOptionsList from './components/groupedOptionsList';
import messages from './messages';

import '../styles.css';

const keyFromName = (name) => kebabCase(name);

export const TransferCreditPoliciesComponent = ({
  onChange,
  initialValue = {},
}) => {
  const [
    selectedGeneralOptions,
    selectedStateOptions,
    selectedCustomOptions,
  ] = useMemo(() => {
    // create temporary lookup map
    const predefinedOptionsLookup = Object.entries(transferCreditPolicies)
      .reduce((acc, [state, options]) => {
        options.forEach((option) => {
          acc[option] =
            state === TRANSFER_CREDIT_POLICIES_GENERAL ? 'general' : 'state';
        });
        return acc;
      }, {});

    const { general, state, custom } = groupBy(
      initialValue?.value || [],
      (value) => predefinedOptionsLookup[value] || 'custom',
    );
    return [general || [], state || [], custom || []];
  }, [initialValue]);
  const [otherCheckboxValue, setOtherCheckboxValue] = useState(
    selectedCustomOptions.length > 0,
  );

  const onGeneralOptionsChange = useCallback(
    (e) => {
      const checked = e.target.checked === true;
      const { name } = e.target;
      const nextGeneralOptions = checked ? union(selectedGeneralOptions, [name]) : without(selectedGeneralOptions, name);
      const nextValue = nextGeneralOptions
        .concat(selectedStateOptions)
        .concat(selectedCustomOptions);
      onChange(e, {
        ...initialValue,
        value: nextValue.sort(transferCreditPoliciesComparator),
      });
    },
    [selectedGeneralOptions, selectedStateOptions, selectedCustomOptions],
  );

  const onStateOptionChange = useCallback(
    (values) => {
      const nextValue = selectedGeneralOptions
        .concat(values)
        .concat(selectedCustomOptions);
      onChange(null, {
        ...initialValue,
        value: nextValue.sort(transferCreditPoliciesComparator),
      });
    },
    [initialValue, selectedGeneralOptions, selectedCustomOptions],
  );

  const onCustomOptionChange = useCallback(
    (values) => {
      const newValue = selectedGeneralOptions
        .concat(selectedStateOptions)
        .concat(values);
      onChange(null, {
        ...initialValue,
        value: newValue.sort(transferCreditPoliciesComparator),
      });
    },
    [selectedGeneralOptions, selectedCustomOptions, selectedStateOptions],
  );

  const onOtherCheckboxChange = useCallback(
    (e) => {
      const checked = !!e.target.checked;
      setOtherCheckboxValue(checked);
    },
    [setOtherCheckboxValue],
  );

  // map of state-specific policies array keyed off the state code (includes all 50 states)
  const allStateOptions = useMemo(() =>
    Object.entries(states).reduce((acc, [code, name]) => {
      acc[name] = transferCreditPolicies[code] || [];
      return acc;
    }, {}), []);
  const allGeneralOptions = transferCreditPolicies[TRANSFER_CREDIT_POLICIES_GENERAL];

  const selectedValues = (initialValue?.value || [])
    .reduce((acc, name) => {
      acc[keyFromName(name)] = true;
      return acc;
    }, {});

  return (
    <div
      styleName="transfer-credit-policies-container"
      role="list"
      data-cy="Premium-Hem-Transfer-Credit-policies-Container"
    >
      <p>
        <FormattedMessage {...messages.creditPoliciesSelectNonStatePolicies} />
      </p>
      {allGeneralOptions.map((name) => {
        const key = keyFromName(name);
        return (
          <Form.Field role="listitem" styleName="field" key={key}>
            <Checkbox
              id={`credit-policies-${key}`}
              label={{
                htmlFor: `credit-policies-${key}`,
                children: name,
              }}
              checked={!!selectedValues[key]}
              name={name}
              onChange={onGeneralOptionsChange}
            />
          </Form.Field>
        );
      })}
      <Checkbox
        data-cy="Premium-Hem-Transfer-Credit-policies-Other"
        key="other"
        id="credit-policies-other"
        label={{
          htmlFor: 'credit-policies-other',
          children: <FormattedMessage {...messages.creditPoliciesOther} />,
        }}
        checked={otherCheckboxValue}
        onChange={onOtherCheckboxChange}
      />
      <TextFieldList
        key="custom-requirements"
        disabled={!otherCheckboxValue}
        values={selectedCustomOptions}
        onChange={onCustomOptionChange}
      />
      <p>
        <FormattedMessage {...messages.creditPoliciesSelectStatePolicies} />
      </p>
      <GroupedOptionsList
        values={selectedStateOptions}
        options={allStateOptions}
        onChange={onStateOptionChange}
        renderEmptyGroup={({ name }) => <span><FormattedMessage {...messages.creditPoliciesNoStatePolicies} /></span>}
      />
    </div>
  );
};

const HemEditorValueType = PropTypes.shape({
  eid: PropTypes.string,
  value: PropTypes.any,
});
TransferCreditPoliciesComponent.propTypes = {
  initialValue: HemEditorValueType,
  onChange: PropTypes.func.isRequired,
};

const TransferCreditPolicies = withCollegeService(
  TransferCreditPoliciesComponent,
  'transferInfo.transferCreditPolicies',
  {},
  {
    value: 'field_he_xfer_adm_policies',
  },
);
export default TransferCreditPolicies;
